'use client';
import React, { useState, useEffect } from 'react';
import { X, Copy, CheckCircle, Loader2, QrCode, Smartphone, AlertCircle, Upload } from 'lucide-react';

const UPI_ID = 'kangleisannathong@axl';

interface UpiPaymentModalProps {
  amount: number;
  purpose: 'registration' | 'wallet_topup';
  orderId: string;
  userId?: string;
  onClose: () => void;
  onSubmitted: (utrNumber: string) => void;
}

export default function UpiPaymentModal({ amount, purpose, orderId, userId, onClose, onSubmitted }: UpiPaymentModalProps) {
  const [tab, setTab] = useState<'qr' | 'manual'>('qr');
  const [utrNumber, setUtrNumber] = useState('');
  const [utrError, setUtrError] = useState('');
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [qrUrl, setQrUrl] = useState('');

  const upiLink = `upi://pay?pa=${UPI_ID}&pn=DropService&am=${amount}&cu=INR&tn=${encodeURIComponent(purpose === 'registration' ? 'Registration Fee' : 'Wallet Top-up')}`;

  useEffect(() => {
    // Generate QR code using a free QR API
    const encoded = encodeURIComponent(upiLink);
    setQrUrl(`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encoded}&bgcolor=ffffff&color=000000&margin=10`);
  }, [upiLink]);

  const handleCopyUpi = () => {
    navigator.clipboard.writeText(UPI_ID).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSubmit = async () => {
    const trimmed = utrNumber.trim();
    if (!trimmed) {
      setUtrError('Please enter your UPI transaction reference number.');
      return;
    }
    if (trimmed.length < 6) {
      setUtrError('Transaction reference must be at least 6 characters.');
      return;
    }
    setUtrError('');
    setSubmitting(true);
    try {
      const res = await fetch('/api/upi/submit-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, utrNumber: trimmed, amount, purpose, userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      onSubmitted(trimmed);
    } catch (err: any) {
      setUtrError(err.message || 'Failed to submit. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={!submitting ? onClose : undefined} />
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm animate-slide-up overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
          <div>
            <h2 className="text-sm font-bold text-foreground">Pay via UPI</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {purpose === 'registration' ? 'One-time registration fee' : 'Wallet top-up'}
            </p>
          </div>
          {!submitting && (
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Amount */}
        <div className="px-5 pt-4 pb-3">
          <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-muted-foreground font-medium">Amount to Pay</span>
            <span className="text-xl font-bold text-primary font-tabular">₹{amount.toLocaleString('en-IN')}</span>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="px-5 pb-3">
          <div className="flex rounded-lg bg-muted p-0.5">
            <button
              onClick={() => setTab('qr')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-semibold transition-all ${tab === 'qr' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <QrCode size={13} /> Scan QR Code
            </button>
            <button
              onClick={() => setTab('manual')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-semibold transition-all ${tab === 'manual' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Smartphone size={13} /> Manual UPI
            </button>
          </div>
        </div>

        <div className="px-5 pb-5 space-y-4">
          {tab === 'qr' && (
            <div className="space-y-3">
              <div className="flex flex-col items-center gap-3">
                <div className="bg-white rounded-xl p-2 border border-border shadow-sm">
                  {qrUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={qrUrl}
                      alt="UPI QR Code for payment"
                      width={200}
                      height={200}
                      className="rounded-lg"
                    />
                  ) : (
                    <div className="w-[200px] h-[200px] flex items-center justify-center">
                      <Loader2 size={24} className="text-primary animate-spin" />
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Scan with any UPI app</p>
                  <p className="text-xs font-semibold text-foreground mt-0.5">PhonePe · GPay · Paytm · BHIM</p>
                </div>
              </div>
              <div className="bg-muted/50 rounded-xl px-3 py-2.5 flex items-center justify-between gap-2">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">UPI ID</p>
                  <p className="text-sm font-bold text-foreground">{UPI_ID}</p>
                </div>
                <button
                  onClick={handleCopyUpi}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors flex-shrink-0"
                >
                  {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {tab === 'manual' && (
            <div className="space-y-3">
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl p-3 space-y-2">
                <p className="text-xs font-bold text-blue-800 dark:text-blue-300">How to pay manually:</p>
                <ol className="text-xs text-blue-700 dark:text-blue-400 space-y-1 list-decimal list-inside">
                  <li>Open your UPI app (PhonePe, GPay, Paytm, etc.)</li>
                  <li>Go to &quot;Send Money&quot; or &quot;Pay by UPI ID&quot;</li>
                  <li>Enter the UPI ID below and pay ₹{amount.toLocaleString('en-IN')}</li>
                  <li>Copy the transaction reference (UTR) and paste below</li>
                </ol>
              </div>
              <div className="bg-muted/50 rounded-xl px-3 py-2.5 flex items-center justify-between gap-2">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">UPI ID</p>
                  <p className="text-sm font-bold text-foreground">{UPI_ID}</p>
                </div>
                <button
                  onClick={handleCopyUpi}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors flex-shrink-0"
                >
                  {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {/* UTR input — always visible */}
          <div className="space-y-2 border-t border-border pt-4">
            <label className="block text-xs font-bold text-foreground uppercase tracking-wide">
              Enter UPI Transaction Reference (UTR)
            </label>
            <p className="text-xs text-muted-foreground">
              After completing payment, enter the 12-digit UTR / transaction ID from your UPI app.
            </p>
            <input
              type="text"
              value={utrNumber}
              onChange={(e) => { setUtrNumber(e.target.value); setUtrError(''); }}
              placeholder="e.g. 123456789012"
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:font-normal"
              disabled={submitting}
            />
            {utrError && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle size={11} /> {utrError}
              </p>
            )}
          </div>

          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2.5">
            <p className="text-xs text-amber-700 dark:text-amber-400">
              ⏳ <strong>Verification takes up to 24 hours.</strong> Your account will be activated once admin confirms your payment.
            </p>
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting || !utrNumber.trim()}
            className="w-full py-3 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {submitting ? (
              <><Loader2 size={15} className="animate-spin" /> Submitting...</>
            ) : (
              <><Upload size={15} /> Submit Payment Reference</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
